/**
 * Feedback-domänlogik
 * Hanterar pingpong-status, sortering och display-helpers för kundfeedback
 */

/**
 * Hämtar senaste tidsstämpel från ett objekt eller tråd
 * @param {Object|Array} obj - Kommentar-objekt eller tråd-array
 * @param {boolean} isThread - Om true, behandla som array av meddelanden
 * @returns {number} - Unix timestamp (millisekunder)
 */
export const getLatestTimestamp = (obj, isThread = false) => {
  if (!obj) return 0;
  
  if (isThread && Array.isArray(obj) && obj.length > 0) {
    const latest = obj[obj.length - 1];
    return latest?.at ? new Date(latest.at).getTime() : 0;
  }
  
  if (typeof obj === 'object') {
    return obj?.respondedAt ? new Date(obj.respondedAt).getTime() : 
           obj?.commentedAt ? new Date(obj.commentedAt).getTime() :
           obj?.at ? new Date(obj.at).getTime() : 0;
  }
  
  return 0;
};

/**
 * Bestämmer om JAG behöver svara på en kommentar (generisk för både kund och byggkonsult)
 * @param {string} commentKey - Nyckeln för kommentaren
 * @param {Object} incomingComments - Den ANDRA personens kommentarer (som jag läser)
 * @param {Object} myResponses - Mina svar på deras kommentarer
 * @returns {boolean} - True om JAG behöver svara
 */
/**
 * Returnerar true om mitt senaste svar är ett opublicerat utkast.
 * Backend sätter draft antingen på parent (BK-style) eller på senaste
 * thread-entry (kund-style). Båda måste detekteras.
 */
const isMyLatestEntryDraft = (myResponseObj) => {
  if (!myResponseObj || typeof myResponseObj !== 'object') return false;
  if (myResponseObj.draft === true) return true; // legacy parent-draft
  if (!Array.isArray(myResponseObj.thread) || myResponseObj.thread.length === 0) return false;
  const lastEntry = myResponseObj.thread[myResponseObj.thread.length - 1];
  return lastEntry?.draft === true;
};

/**
 * Returnerar publicerade thread-entries (filtrerar bort drafts från slutet).
 */
export const needsReply = (commentKey, incomingComments, myResponses) => {
  const incomingMsg = incomingComments[commentKey];
  const incomingText = typeof incomingMsg === 'object' ? (incomingMsg?.text || '') : (incomingMsg || '');
  const incomingThread = Array.isArray(incomingMsg?.thread) ? incomingMsg.thread : [];

  const myResponseObj = myResponses?.[commentKey];

  // Utkast (draft) räknas INTE som ett riktigt svar — kollar både parent-flagga
  // (BK-style) och senaste thread-entry (kund-style)
  const isDraft = isMyLatestEntryDraft(myResponseObj);

  // Om jag har ett opublicerat utkast → räknas alltid som "behöver action"
  // (jag måste klicka Skicka för att publicera det)
  if (isDraft) return true;

  const myText = myResponseObj ? (typeof myResponseObj === 'object' ? (myResponseObj?.text || '') : (myResponseObj || '')) : '';
  const myThread = Array.isArray(myResponseObj?.thread) ? myResponseObj.thread : [];

  const hasMyReply = Boolean(myText) || myThread.length > 0;

  const incomingLatest = Math.max(getLatestTimestamp(incomingMsg), getLatestTimestamp(incomingThread, true));
  const myLatest = Math.max(getLatestTimestamp(myResponseObj), getLatestTimestamp(myThread, true));

  const theyRepliedLast = incomingLatest > myLatest;

  return theyRepliedLast || (!hasMyReply && incomingText);
};

/**
 * BAKÅTKOMPATIBILITET: Alias för needsReply med gamla parameternamn
 * @deprecated Använd needsReply istället
 */
export const needsConsultantReply = (commentKey, customerComments, byggkonsultResponses) => {
  return needsReply(commentKey, customerComments, byggkonsultResponses);
};

/**
 * Bestämmer om någon kommentar behöver svar (generisk)
 * @param {Object} incomingComments - Den ANDRA personens kommentarer
 * @param {Object} myResponses - Mina svar
 * @returns {boolean} - True om minst en kommentar behöver svar
 */
export const hasAnyUnansweredComments = (incomingComments, myResponses) => {
  const keys = Object.keys(incomingComments || {}).filter(k => k !== '__meetingRequest__');

  return keys.some(commentKey => {
    // Kolla om någon (jag ELLER de) har markerat som OK
    const iMarkedAsOK = isMarkedAsOK(commentKey, myResponses);
    const theyMarkedAsOK = isMarkedAsOK(commentKey, incomingComments);

    // Om OK är markerat MEN motparten har lagt till nytt innehåll efter —
    // ignorera OK-markeringen så vi fortsätter räkna som obesvarat
    const okIsCurrent = (iMarkedAsOK || theyMarkedAsOK) && !isOKStale(commentKey, incomingComments, myResponses);

    if (okIsCurrent) {
      return false;
    }

    return needsReply(commentKey, incomingComments, myResponses);
  });
};

/**
 * Sorterar kommentarsnycklar så osvarade kommer först (generisk)
 * @param {string[]} keys - Array av kommentarsnycklar
 * @param {Object} incomingComments - Den ANDRA personens kommentarer
 * @param {Object} myResponses - Mina svar
 * @returns {string[]} - Sorterad array av nycklar
 */
export const sortCommentsByUrgency = (keys, incomingComments, myResponses) => {
  return keys.sort((a, b) => {
    // Generell feedback alltid överst
    if (a === '__overall__') return -1;
    if (b === '__overall__') return 1;
    
    // För övriga: prioritera osvarade
    // Men ta hänsyn till om någon har markerat som OK — och att markeringen
    // inte är stale (motparten har svarat efteråt)
    const aHasOK = isMarkedAsOK(a, myResponses) || isMarkedAsOK(a, incomingComments);
    const bHasOK = isMarkedAsOK(b, myResponses) || isMarkedAsOK(b, incomingComments);
    const aMarkedOK = aHasOK && !isOKStale(a, incomingComments, myResponses);
    const bMarkedOK = bHasOK && !isOKStale(b, incomingComments, myResponses);

    // Om någon har markerat som OK (och det fortfarande gäller), räknas det INTE som osvarad
    const aNeedsReply = !aMarkedOK && needsReply(a, incomingComments, myResponses);
    const bNeedsReply = !bMarkedOK && needsReply(b, incomingComments, myResponses);
    
    // Osvarade först
    if (aNeedsReply && !bNeedsReply) return -1;
    if (!aNeedsReply && bNeedsReply) return 1;
    
    // Behåll ursprunglig ordning om samma status
    return 0;
  });
};

/**
 * Hämtar display-namn för en kommentar
 * @param {string} commentKey - Kommentarsnyckel (t.ex. 'byggoffert_item1' eller '__overall__')
 * @param {Object} formData - FormData med estimat-kategorier
 * @returns {string} - Mänskligt läsbart namn
 */
export const getCommentDisplayName = (commentKey, formData) => {
  if (commentKey === '__overall__') {
    return 'Generell feedback på estimat';
  }
  if (commentKey === '__budget__') {
    return 'Matchar detta er önskade budget?';
  }
  if (commentKey === '__prioritering__') {
    return 'Är det någon del vi ska prioritera bort/ned/upp?';
  }

  try {
    const firstUnderscore = commentKey.indexOf('_');
    if (firstUnderscore > 0) {
      const categoryKey = commentKey.slice(0, firstUnderscore);
      const formItemKey = commentKey.slice(firstUnderscore + 1);
      
      const item = formData?.estimat?.[categoryKey]?.[formItemKey];
      
      if (item && typeof item.label === 'string' && item.label.length >= 0) {
        return item.label;
      }
      
      // Fallback: om underkategori finns (t.ex. invändigt_Arbetsmoment)
      const parts = formItemKey.split('_');
      return parts.length > 1 ? parts.slice(1).join(' ') : formItemKey;
    }
  } catch (_) {
    // Ignore parsing errors
  }
  
  return commentKey;
};

/**
 * Mergear kundens och konsultens konversation i kronologisk ordning.
 *
 * Hanterar två kända problem:
 * 1) BK:s "current text" finns ofta som sista posten i consThread → läggs inte till dubbelt.
 * 2) DB-tråden kan innehålla faktiska duplikat (samma text+by efter varandra) → dedupliceras
 *    så att endast den FÖRSTA av konsekutiva dubletter behålls.
 *
 * @param {Object|string} customerComment - Kundens kommentar (med thread)
 * @param {Object|string} consultantResponse - Byggkonsultens svar (med thread)
 * @returns {Array} - Sorterad och dedupliсerad array av meddelanden
 */
export const mergeConversationThread = (customerComment, consultantResponse) => {
  const custThread = Array.isArray(customerComment?.thread)
    ? customerComment.thread
    : [];

  const consText = consultantResponse
    ? (typeof consultantResponse === 'object'
        ? (consultantResponse?.text || '')
        : (consultantResponse || ''))
    : '';
  const consAt = (consultantResponse && typeof consultantResponse === 'object' && (consultantResponse?.respondedAt || consultantResponse?.at))
    ? (consultantResponse.respondedAt || consultantResponse.at)
    : null;
  const consName = (consultantResponse && typeof consultantResponse === 'object' && consultantResponse?.author?.name)
    ? consultantResponse.author.name
    : 'Byggkonsult';
  const consThread = Array.isArray(consultantResponse?.thread)
    ? consultantResponse.thread
    : [];

  // Inkludera consText som en separat post BARA om den inte redan finns i consThread
  const consTextInThread = consText && consThread.some(t => (t?.text || '').trim() === consText.trim());
  const consTextEntry = (consText && !consTextInThread)
    ? [{ by: 'consultant', text: consText, at: consAt, author: { name: consName } }]
    : [];

  // Sortera kronologiskt
  const merged = [
    ...consTextEntry,
    ...custThread,
    ...consThread,
  ].filter(Boolean).sort((a, b) => {
    const ta = a?.at ? new Date(a.at).getTime() : 0;
    const tb = b?.at ? new Date(b.at).getTime() : 0;
    return ta - tb;
  });

  // Dedupliсera ALLA dubletter (samma by + samma exakta text). Behåller den FÖRSTA förekomsten.
  // Detta löser DB-buggen där samma meddelande råkar sparas flera gånger i tråden.
  const seen = new Set();
  const deduped = [];
  for (const msg of merged) {
    const key = `${msg.by}::${(msg.text || '').trim()}`;
    if (!key.endsWith('::') && seen.has(key)) continue;
    seen.add(key);
    deduped.push(msg);
  }

  return deduped;
};

/**
 * Extraherar författar-info från ett meddelande
 * @param {Object|string} comment - Kommentar-objekt eller sträng
 * @param {string} defaultName - Default-namn om inget finns
 * @returns {Object} - { name, at }
 */
export const getAuthorInfo = (comment, defaultName = 'Okänd') => {
  if (typeof comment === 'object') {
    return {
      name: comment?.author?.name || defaultName,
      at: comment?.commentedAt || comment?.respondedAt || comment?.at || null
    };
  }
  return { name: defaultName, at: null };
};

/**
 * Bestämmer om en kommentar är markerad som OK (generisk)
 * @param {string} commentKey - Nyckeln för kommentaren
 * @param {Object} myResponses - Mina svar (kan vara antingen byggkonsult eller kund)
 * @returns {boolean} - True om JAG har markerat som OK
 */
export const isMarkedAsOK = (commentKey, myResponses) => {
  const myResponseObj = myResponses?.[commentKey];
  const responseText = myResponseObj
    ? (typeof myResponseObj === 'object'
        ? (myResponseObj?.text || '')
        : (myResponseObj || ''))
    : '';
  return responseText === '✓ Markerad som OK';
};

/**
 * Kolla om en OK-markering är stale — dvs motparten har lagt till
 * nytt innehåll efter att OK:et sattes. Används för att återaktivera
 * "Ej svarat"-status när kund svarar på ny estimatversion.
 */
export const isOKStale = (commentKey, incomingComments, myResponses) => {
  const iMarkedAsOK = isMarkedAsOK(commentKey, myResponses);
  const theyMarkedAsOK = isMarkedAsOK(commentKey, incomingComments);
  if (!iMarkedAsOK && !theyMarkedAsOK) return false;

  // Tidsstämpel för OK-markeringen (senast satt av den sida som markerade)
  const okSide = iMarkedAsOK ? myResponses : incomingComments;
  const okObj = okSide?.[commentKey];
  const okTimestamp = getLatestTimestamp(okObj);

  // Tidsstämpel för motpartens senaste innehåll (inkl tråd)
  const otherSide = iMarkedAsOK ? incomingComments : myResponses;
  const otherObj = otherSide?.[commentKey];
  const otherLatest = Math.max(
    getLatestTimestamp(otherObj),
    getLatestTimestamp(otherObj?.thread, true)
  );

  // OK är stale om motparten har nyare innehåll
  return otherLatest > okTimestamp;
};

/**
 * Får status-text för en kommentar (generisk)
 * @param {string} commentKey - Nyckeln för kommentaren
 * @param {Object} incomingComments - Den ANDRA personens kommentarer
 * @param {Object} myResponses - Mina svar
 * @returns {string} - 'Ej svarat', 'Markerat som OK', eller 'Svarat'
 */
export const getCommentStatus = (commentKey, incomingComments, myResponses) => {
  // Kolla "Markerat som OK" FÖRST (prioritet 1)
  // Kolla BÅDE om JAG eller DE har markerat som OK
  const iMarkedAsOK = isMarkedAsOK(commentKey, myResponses);
  const theyMarkedAsOK = isMarkedAsOK(commentKey, incomingComments);

  // Men bara om OK-markeringen fortfarande är aktuell — om motparten
  // har lagt till nytt innehåll efter OK:et ska statusen gå tillbaka
  // till "Ej svarat" (t.ex. kund svarar på generell feedback i ny version)
  if ((iMarkedAsOK || theyMarkedAsOK) && !isOKStale(commentKey, incomingComments, myResponses)) {
    return 'Markerat som OK';
  }

  // Kolla om motpartens kommentar är ett utkast som ej skickats än
  // (backend markerar med _allDraft när alla thread-rader är drafts)
  const incomingObj = incomingComments?.[commentKey];
  const isIncomingAllDraft = incomingObj && typeof incomingObj === 'object' && incomingObj._allDraft === true;
  if (isIncomingAllDraft) {
    return 'Kunds utkast';
  }

  // Kolla om mitt svar är ett utkast (sparat men ej skickat)
  // Per-rad draft-flagga är källan till sanning; parent-flagga är legacy
  const myResponseObj = myResponses?.[commentKey];
  if (isMyLatestEntryDraft(myResponseObj)) {
    return 'Utkast';
  }

  // Sedan kolla om JAG behöver svara
  const iNeedToReply = needsReply(commentKey, incomingComments, myResponses);

  if (iNeedToReply) {
    return 'Ej svarat';
  }

  return 'Svarat';
};

